import { useMemo, useState, MouseEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  MRT_Column,
  MRT_GrabHandleButton,
  MRT_RowData,
  MRT_TableInstance,
} from "material-react-table";

interface Props<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
}

/**
 * Replacement for MRT_ShowHideColumnsButton that omits the pin/unpin UI.
 * Supports drag-to-reorder for columns whose `enableColumnOrdering` is not
 * explicitly disabled (so the projectName column — which sets it to false —
 * has no grab handle and cannot be moved).
 */
export function ShowHideColumnsButton<TData extends MRT_RowData>({
  table,
}: Props<TData>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [hoveredColumnId, setHoveredColumnId] = useState<string | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const {
    getCenterLeafColumns,
    getLeftLeafColumns,
    getRightLeafColumns,
    getIsAllColumnsVisible,
    getIsSomeColumnsVisible,
    getState,
    setColumnOrder,
    options: { enableColumnOrdering },
  } = table;
  const { columnOrder } = getState();

  const leftColumns = getLeftLeafColumns();
  const centerColumns = getCenterLeafColumns();
  const rightColumns = getRightLeafColumns();
  const orderedColumns = useMemo(() => {
    const centerById = new Map(centerColumns.map((c) => [c.id, c] as const));
    const center = columnOrder.length
      ? Array.from(new Set(columnOrder))
          .map((id) => centerById.get(id))
          .filter((c): c is MRT_Column<TData> => Boolean(c))
      : centerColumns;
    return [...leftColumns, ...center, ...rightColumns];
  }, [columnOrder, centerColumns, leftColumns, rightColumns]);

  const toggleAll = (value: boolean) => {
    orderedColumns
      .filter((col) => col.columnDef.enableHiding !== false)
      .forEach((col) => col.toggleVisibility(value));
  };

  const canReorder = (column: MRT_Column<TData>) =>
    Boolean(enableColumnOrdering) &&
    column.columnDef.enableColumnOrdering !== false;

  const handleDragStart = (column: MRT_Column<TData>) => {
    setDraggingColumnId(column.id);
  };

  const handleDragEnd = () => {
    if (draggingColumnId && hoveredColumnId && columnOrder.length) {
      const next = [...columnOrder];
      const fromIndex = next.indexOf(draggingColumnId);
      const toIndex = next.indexOf(hoveredColumnId);
      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
        setColumnOrder(next);
      }
    }
    setDraggingColumnId(null);
    setHoveredColumnId(null);
  };

  const handleDragEnter = (column: MRT_Column<TData>) => () => {
    if (draggingColumnId && canReorder(column)) {
      setHoveredColumnId(column.id);
    }
  };

  return (
    <>
      <Tooltip title="Show/Hide columns">
        <IconButton
          aria-label="Show/Hide columns"
          onClick={handleOpen}
          size="medium"
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1 }}>
          <Button
            disabled={!getIsSomeColumnsVisible()}
            onClick={() => toggleAll(false)}
          >
            Hide all
          </Button>
          <Button
            disabled={getIsAllColumnsVisible()}
            onClick={() => toggleAll(true)}
          >
            Show all
          </Button>
        </Box>
        <Divider />
        {orderedColumns.map((column) => {
          const header = column.columnDef.header;
          if (!header || column.columnDef.visibleInShowHideMenu === false) {
            return null;
          }
          const reorderable = canReorder(column);
          const isHovered = hoveredColumnId === column.id;
          const isDragging = draggingColumnId === column.id;
          return (
            <MenuItem
              key={column.id}
              disableRipple
              onDragEnter={reorderable ? handleDragEnter(column) : undefined}
              sx={(theme) => ({
                alignItems: "center",
                gap: 1,
                py: "6px",
                opacity: isDragging ? 0.5 : 1,
                outline: isHovered
                  ? `2px dashed ${theme.palette.primary.main}`
                  : "none",
                outlineOffset: "-2px",
              })}
            >
              {reorderable ? (
                <MRT_GrabHandleButton
                  onDragStart={() => handleDragStart(column)}
                  onDragEnd={handleDragEnd}
                  table={table}
                />
              ) : (
                <Box sx={{ width: "28px" }} />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={column.getIsVisible()}
                    onChange={() => column.toggleVisibility()}
                  />
                }
                disabled={!column.getCanHide()}
                label={String(header)}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default ShowHideColumnsButton;
